import React, { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useApp } from './AppContext';
import { useAuth } from './AuthContext';
import type { UserConfig, User } from '../types/config';
import { getConfig } from '../config';

/**
 * Context for passing handler configuration and app state to ipa-core page components
 * This replicates the pattern from the old AppProvider where components receive
 * handler, user, userConfig, selectedItems, and actions
 */

export interface IpaHandler {
  config?: Record<string, any>;
  title?: string;
  path?: string;
  pageComponent?: string;
  icon?: string;
  [key: string]: any;
}

export interface SelectedItems {
  [key: string]: any;
  ipaConfig?: any;
  selectedProject?: any;
  selectedUserGroup?: any;
  selectedUserGroupId?: string;
}

export interface IpaPageActions {
  reloadConfig: () => Promise<void>;
  restartApp: () => Promise<void>;
  setSelectedItems: (items: Partial<SelectedItems>) => void;
  userLogout: () => Promise<void>;
  getCurrentHandler: () => IpaHandler | null;
  handlePageHandlerLoadError: (error: Error) => void;
}

interface IpaPageContextValue {
  // Core data
  handler: IpaHandler;
  userConfig: UserConfig | null;
  user: User | null;
  selectedItems: SelectedItems;

  // Actions
  actions: IpaPageActions;
}

const IpaPageContext = createContext<IpaPageContextValue | undefined>(undefined);

interface IpaPageProviderProps {
  children: ReactNode;
  handler: IpaHandler;
}

/**
 * Provider for ipa-core page components
 * Wraps each page component and provides handler, user, config, and actions
 */
export function IpaPageProvider({ children, handler }: IpaPageProviderProps) {
  const { userConfig, selectedProject, selectedUserGroup } = useApp();
  const { user, logout } = useAuth();
  const config = getConfig();
  const appId = config.applicationId;

  // Debug: Check if user is available
  console.log('IpaPageProvider - user:', user);

  // Load selectedItems from localStorage
  const selectedItems = useMemo((): SelectedItems => {
    const storageKey = `ipadt_selectedItems${appId}`;
    let items: SelectedItems = {};

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        items = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load selectedItems from localStorage:', error);
    }

    // Merge with current selections
    return {
      ...items,
      selectedProject: selectedProject || items.selectedProject,
      selectedUserGroup: selectedUserGroup || items.selectedUserGroup,
      selectedUserGroupId: selectedUserGroup?._id || items.selectedUserGroupId,
    };
  }, [selectedProject, selectedUserGroup, appId]);

  // Save selectedItems to localStorage
  const setSelectedItems = useCallback((newItems: Partial<SelectedItems>) => {
    const storageKey = `ipadt_selectedItems${appId}`;

    try {
      const currentItems = selectedItems;
      const updatedItems = { ...currentItems, ...newItems };
      localStorage.setItem(storageKey, JSON.stringify(updatedItems));
    } catch (error) {
      console.warn('Failed to save selectedItems to localStorage:', error);
    }
  }, [selectedItems, appId]);

  // Reload config without restarting
  const reloadConfig = useCallback(async () => {
    console.log('reloadConfig - not implemented yet');
    // TODO: Implement config reload if needed
  }, []);

  // Restart app (full reload)
  const restartApp = useCallback(async () => {
    console.log('restartApp - full page reload');
    window.location.reload();
  }, []);

  // User logout
  const userLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  // Get current handler
  const getCurrentHandler = useCallback(() => {
    return handler;
  }, [handler]);

  // Handle page handler load errors
  const handlePageHandlerLoadError = useCallback((error: Error) => {
    console.error('Page handler load error:', error);
    throw error;
  }, []);

  // Build actions object
  const actions = useMemo<IpaPageActions>(() => ({
    reloadConfig,
    restartApp,
    setSelectedItems,
    userLogout,
    getCurrentHandler,
    handlePageHandlerLoadError,
  }), [reloadConfig, restartApp, setSelectedItems, userLogout, getCurrentHandler, handlePageHandlerLoadError]);

  const value: IpaPageContextValue = {
    handler,
    userConfig,
    user,
    selectedItems,
    actions,
  };

  return (
    <IpaPageContext.Provider value={value}>
      {children}
    </IpaPageContext.Provider>
  );
}

export function useIpaPage(): IpaPageContextValue {
  const context = useContext(IpaPageContext);
  if (context === undefined) {
    throw new Error('useIpaPage must be used within an IpaPageProvider');
  }
  return context;
}

/**
 * HOC to wrap ipa-core components with full AppProvider-style props
 * This matches the pattern from the old withGenericPage HOC
 */
export function withIpaPageContext<P extends object>(
  Component: React.ComponentType<P>,
  handler: IpaHandler
) {
  return function WrappedComponent(props: P) {
    return (
      <IpaPageProvider handler={handler}>
        <IpaPageConsumer Component={Component} props={props} />
      </IpaPageProvider>
    );
  };
}

/**
 * Consumer component that passes context as props
 * This ensures components receive the data both via context and props
 */
function IpaPageConsumer<P extends object>({
  Component,
  props
}: {
  Component: React.ComponentType<P>;
  props: P;
}) {
  const pageContext = useIpaPage();

  // Merge page context into props (old components expect these as props)
  // Start with pageContext, then let explicitly passed props override
  // BUT ensure user, userConfig, actions are always from context
  const enhancedProps = {
    ...pageContext,  // Base context (handler, userConfig, user, selectedItems, actions)
    ...props,        // Explicit props override (config, handler, selectedItems, onLoadComplete)
    // Force these to always come from context, never be overridden
    user: pageContext.user,
    userConfig: pageContext.userConfig,
    actions: pageContext.actions,
  } as P;

  // Debug: Log what we're passing
  console.log('IpaPageConsumer - user:', (enhancedProps as any).user);
  console.log('IpaPageConsumer - keys:', Object.keys(enhancedProps).join(', '));

  return <Component {...enhancedProps} />;
}
